import { Button } from '@progress/kendo-react-buttons';
import { DragAndDrop, getter, Keys } from '@progress/kendo-react-common';
import {
  Grid as KendoGrid,
  GridCellProps,
  GridColumn,
  GridColumnResizeEvent,
  GridEvent,
  GridNoRecords,
  GridRowClickEvent,
  GridRowProps,
  GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import {
  GridRowDoubleClickEvent
} from '@progress/kendo-react-grid/dist/npm/interfaces/events';
import classnames from 'classnames';
import * as _ from 'lodash';
import uuid from 'uuid-random';
import React, { KeyboardEvent, ReactElement } from 'react';
import Component from '../../dispatcher/modules/component/Component';
import ColumnMenu from './columnmenu/ColumnMenu';
import {
  Position,
  RequestDataBase,
  SearchRecordBy
} from '../../dispatcher/Types';
import QuerySelector from '../../utility/QuerySelector';
import {
  FOCUSABLE_ELEMENTS_WITHIN_TR,
  getNodesFromContainerFromType,
  getNodesFromContainerFromTypes,
  getSortedData,
  insert,
  isFocusedElementHasTabIndex
} from '../../utility';
import CellActions from './cell_renderer/CellActions';
import CellButton from './cell_renderer/CellButton';
import CellCheckbox from './cell_renderer/CellCheckbox';
import CellCount from './cell_renderer/CellCount';
import CellIcon from './cell_renderer/CellIcon';
import CellTag from './cell_renderer/CellTag';
import { EMPTY_GRID_CLASS } from '../../utility/Constants';
import CellProgress from './cell_renderer/CellProgress';
import CellLink from './cell_renderer/CellLink';
import CellRouteLink from './cell_renderer/CellRouteLink';
import { Skeleton } from '@progress/kendo-react-indicators';
import CellDate from './cell_renderer/CellDate';
import CellText from './cell_renderer/CellText';
import HeaderCellIcon from './header_cell_renderer/HeaderCellIcon';
import RowSkeleton from './skeleton/RowSkeleton';
import HeaderCell from './header_cell_renderer/HeaderCell';
import CellSegment from './cell_renderer/CellSegment';
import CellReorderingRows from './cell_renderer/CellReorderingRows';
import CellTextCounterTag from './cell_renderer/CellTextCounterTag';
import HeaderCellCheckbox from './header_cell_renderer/HeaderCellCheckbox';
import { CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import DraggableRow from './reordering_rows/DraggableRow';
import { ReorderContext } from './reordering_rows/ReorderContext';
import { ScrollCorrectionServiceVS } from './VirtualScrollFixed';
import { SortDescriptor } from '@progress/kendo-data-query';
import {
  FILTER_AND_SORT_DATA,
  NAVIGATION_GRID_PROPS,
  NAVIGATION_GRID_STATE,
  SELECT_WITHIN_ROW,
  TAB_KEY_EVENT_PROPS
} from './Types';

import {
  VirtualScrollFixed
} from './VirtualScrollFixedKendo';
import CellExpandDetails from './cell_renderer/CellExpandDetails';
import CellEllipsisExpandText from './cell_renderer/CellEllipsisExpandText';

class NavigationBaseGrid extends Component<NAVIGATION_GRID_PROPS, NAVIGATION_GRID_STATE> {
  private enabledInputElementTypes: string[] = ['button', 'checkbox', 'radio'];
  holdingArrowKeys: boolean = false;
  lastSelectedIndex: number = -1;
  lastClickedIndex: number = -1;
  private selectedData: any[] = [];
  private readonly tabKeyInitEventProps: TAB_KEY_EVENT_PROPS = {
    currentScrollTop: 0,
    holding: false,
    shift: false,
    changedScrollTop: false,
    waitScrollCounter: 0,
    waitScrollNumber: 10,
    intoContainer: false
  };
  private tabKeyEventProps: TAB_KEY_EVENT_PROPS;
  private previousAction: string | null = null;
  private gridRef: any;
  private accessibilityRowDescriptionRef: any;
  private accessibilityCaptionElement: QuerySelector;
  private headerContentAlign = {
    center: 'k-text-center',
    left: 'k-text-left',
    right: 'k-text-right'
  };
  private focusElementWithinRow: SELECT_WITHIN_ROW = SELECT_WITHIN_ROW.none;
  private rowSkeletonConfig = [];
  public requestConfig: RequestDataBase;
  private selectableAllUniqueField: string = 'Index';
  private lastSelectedRecord: any = null;
  private isTotalChangedFromAnotherUser: boolean = false;
  public isCollapsedAll: boolean = false;
  public isExpandedAll: boolean = false;
  /**
   * Added new record from Navigation Base Editor Grid
   */
  addedNewRecord: any = undefined;
  /**
   * If true keyDown listener will be hundled
   */
  enableNavigation: boolean = true;
  isPageDownOrPageUpInProgress: boolean = false;
  isKeyEndOrKeyHomeInProgress: boolean = false;
  refocus: {
    isFirst?: boolean,
    rowSelection?: boolean
  } = {
    isFirst: undefined,
    rowSelection: undefined
  };
  state: NAVIGATION_GRID_STATE = {
    pageDataPopulated: [0],
    pageNumber: 0,
    skip: 0,
    tasks: {}, // TODO add option to pass via props
    data: [],
    dataLoadDone: false,
    requestInProgress: false,
    total: 0,
    addedTotal: 0,
    filterData: {},
    columns: [],

    showContextMenu: false,
    offset: {
      left: 0,
      top: 0
    },
    contextMenuItem: undefined,
    activeRowIndex: undefined,
    sort: [],
    selectedState: {},
    activeItem: null,
    currentFilterValue: ''
  };
  scrollCorrectionServiceVS: ScrollCorrectionServiceVS = new ScrollCorrectionServiceVS();
  currentScrollToIndex: number = -1;

  static defaultProps = {
    filterAction: 'columnFilterChanged',
    sortAction: 'columnSortChanged',
    requestActionName: 'refreshAnyNavigationGridData',
    requestConfig: null
  };

  constructor(props) {
    super(props);
    if (!this.moduleId) {
      this.moduleId = uuid();
    }
    if (!_.isEmpty(this.props.data)) {
      let data = [...this.props.data];
      if (props.selectableAll) {
        const selectedState = {};
        data.forEach((item) => {
          selectedState[this.getIdGetter()(item)] = item[props.selectableAll];
        });
        this.state.selectedState = { ...selectedState };
      }
      data = this.getDisabledAllCheckboxData(data).data;
      this.state.data = [...data];
    }
    if (!_.isEmpty(props.preselectedRowIds)) {
      let preselectedState = {};
      props.preselectedRowIds.forEach(preselectedRowId => preselectedState[preselectedRowId] = true);
      this.state.selectedState = {
        ...this.state.selectedState,
        ...preselectedState
      };
    }
    if (!_.isEmpty(this.props.filterData)) {
      this.state.filterData = { ...this.props.filterData };
    }
    this.tabKeyEventProps = { ...this.tabKeyInitEventProps };
    this.accessibilityRowDescriptionRef = React.createRef();

    this.state = {
      ...this.state,
      columns: this.props.columns
    };

    this.requestConfig = _.cloneDeep(props.requestConfig);
    if (this.requestConfig && _.isUndefined(this.requestConfig?.params)) {
      this.requestConfig.params = {};
    }
    if (this.requestConfig && !_.isUndefined(this.requestConfig?.paramsOnMounting)) {
      _.assign(this.requestConfig.params, this.requestConfig.paramsOnMounting);
      this.requestConfig.paramsOnMounting = {};
    }
  }

  private navigationGridModuleName = this.props.isTree ? 'NavigationTreeGrid' : this.moduleName

  /**
   * Sets new value to lastSelectedIndex property and
   * executes onRowDeselected
   * @param {Number} value
   */
  setLastSelectedIndexValue = (value) => {
    const { onRowDeselected } = this.props;

    this.lastSelectedIndex = value;
    if (this.lastSelectedIndex === -1 && onRowDeselected) {
      onRowDeselected();
    }
  }

  /**
   * Returns selected row dataItem
   */
  getSelectedRowDataItem = () => this.lastSelectedRecord ?? null

  /**
   * Sets selected row dataItem
   * @param {any} record Row dataItem record
   */
  setSelectedRowDataItem = (record?: any) => {
    this.lastSelectedRecord = record;
    if (_.isEmpty(this.lastSelectedRecord) && this.lastSelectedIndex !== -1) {
      this.lastSelectedRecord = this.state.data[this.lastSelectedIndex];
    }
    if (_.isEmpty(this.lastSelectedRecord)) {
      this.lastSelectedRecord = null;
    }
  }

  /**
   * Sets disabled property to all of data item as true
   * @param {any[]} data
   */
  getDisabledAllCheckboxData = (data) => {
    const checkBoxColumn = this.props.columns.find((column) => {
      const { cellType, dataIndex, field: columnField } = column;
      return (_.isUndefined(cellType) ? (columnField || dataIndex) : cellType) === 'checkbox';
    });
    const checkBoxDisabled = checkBoxColumn?.disabled;
    if (checkBoxDisabled) {
      data = data.map(dataItem => ({
        ...dataItem,
        disabled: checkBoxDisabled
      }));
    }
    if (this.props.selectableAll) {
      let selectedState = {};
      data.forEach((item) => {
        selectedState[this.getIdGetter()(item)] = !!item.checkbox;
      });
      return {
        data,
        selectedState
      };
    }
    return { data };
  }

  assignParams = (params: any, assignedParams: boolean = false) => {
    if (!assignedParams) {
      _.set(this.requestConfig, 'params', params);
    } else {
      _.assign(this.requestConfig?.params, params);
    }
  }

  /**
   * Change state to close context menu
   */
  closeContextMenu = (focusLastRow = true) => {
    this.setState({
      showContextMenu: false
    }, () => {
      if (focusLastRow) {
        this.focusLastSelectedIndexRow(true);
      }
    });
  };

  /**
   * onScroll Grid handler
   * If ContextMenu is opened, close it when user scrolls
   */
  closeContextMenuOnScroll = () => {
    if (this.state.showContextMenu) {
      this.closeContextMenu();
    }
  };

  createRefreshData = () => {
    let navigationGridModuleName = this.props.gridType === 'tree' ? 'NavigationTreeGrid' : this.moduleName;
    if (!this.props.isVirtualScrollHOC) {
      navigationGridModuleName = navigationGridModuleName === 'NavigationTreeGrid' ? 'NavigationBaseTreeGrid' : 'NavigationBaseGrid';
    }
    return {
      navigationGridModuleId: this.moduleId,
      navigationGridModuleName,
      scrollRequestParams: this.prepareRequestParams(0),
      ...this.props.requestConfig
    };
  };

  findColumn = (field: string) => {
    let columnConfig;
    this.props.columns.forEach((column) => {
      if (!columnConfig) {
        const { cellType, dataIndex, field: columnField } = column;
        if ((_.isUndefined(cellType) ? (columnField || dataIndex) : dataIndex) === field) {
          columnConfig = column;
        } else {
          column.subColumns?.forEach((subColumn) => {
            const { cellType, dataIndex, field: columnField } = subColumn;
            if ((_.isUndefined(cellType) ? (columnField || dataIndex) : dataIndex) === field) {
              columnConfig = subColumn;
            }
          });
        }
      }
    });
    return columnConfig;
  }

  /**
   * Returns filter data from grid columns
   * @param {Boolean} isQuery If true it returns values as string, in other case it returns values as array
   */
  getFilterData = (isQuery = false) => {
    const { filterData } = this.state;
    const data: Record<string, string | string[]> = {};
    let values;

    for (let prop in filterData) {
      if (filterData.hasOwnProperty(prop)) {
        ({ values } = filterData[prop]);
        if (!_.isEmpty(values)) {
          data[prop] = isQuery ? values.join() : values.slice();
        }
      }
    }

    return data;
  }

  onFilterChange = (field: string | string[], values: string[]) => {
    const filterData = {
      ...this.state.filterData
    };
    let filter;
    if (_.isArray(field)) {
      filter = {
        ...filterData[[...field].shift()],
        values
      };
      field.forEach((item) => {
        filterData[item] = filter;
      });
    } else {
      filter = {
        ...filterData[field],
        values
      };
      filterData[field] = filter;
    }

    if (_.isFunction(this.props.onFilterAndSortChange)) {
      let params = {};

      for (const name in filterData) {
        if (!_.isEmpty(filterData[name]?.values)) {
          params[name] = filterData[name]?.values.join(',');
        }
      }

      const data: FILTER_AND_SORT_DATA = {
        params,
        filterType: 'onFilterChange',
        isFilterChanged: !_.isEmpty(params),
        filterData
      };

      this.props.onFilterAndSortChange(data);
    }

    this.setStateWithAction({
      filterData
    }, this.props.filterAction, this.createRefreshData);
  };

  onSortChange = ({ sort }) => {
    if (_.isFunction(this.props.onSortChange)) {
      this.props.onSortChange({ sort });
    } else {
      let sortable = _.isEmpty(sort);
      let mappingSortName;
      if (!_.isEmpty(sort)) {
        const column = this.findColumn(sort[0].field);
        const { columnMenuConfig } = column;
        mappingSortName = columnMenuConfig?.mappingSortName;
        sortable = columnMenuConfig && columnMenuConfig.sortable !== false;
      }

      if (sortable) {
        if (mappingSortName) {
          sort[0].fieldMapName = mappingSortName;
        }
        const newState = this.props.data ? {
          sort
        } : {
          sort,
          data: [],
          pageNumber: 0,
          pageDataPopulated: [],
          dataLoadDone: false,
          addedTotal: 0,
          skip: 0
        };

        if (_.isFunction(this.props.onFilterAndSortChange)) {
          let params = {};

          if (sort[0]?.field) {
            const sortParams = sort[0];
            params = {
              sort: `${sortParams.field},${sortParams.dir}`
            };
          }

          const data: FILTER_AND_SORT_DATA = {
            filterType: 'onSortChange',
            params,
            sort
          };

          this.props.onFilterAndSortChange(data);
        }

        this.setStateWithAction(newState, this.props.sortAction, this.createRefreshData);
      }
    }
  };

  /**
   * Set sort
   * @param {SortDescriptor[]} sort
   */
  public setSort = (sort: SortDescriptor[]): void => {
    this.setState({ sort });
  }

  /**
   * Sorts state data by asc or desc ordering
   * @param {String} params There are two words in params that are separated by a comma (eg. 'column_name,desc')
   */
  localSort = (params: string) => {
    const sortParams = params.split(',');
    const field = sortParams.shift();
    const sortType = sortParams.shift();
    let data = [...this.state.data];
    const sortedData = getSortedData(data, field, sortType);
    if (this.props.getSortedData) {
      data = this.props.getSortedData(data, field, sortType, sortedData);
    } else {
      data = sortedData;
    }
    this.setState({
      data
    });
  }

  private cellRender = (cellProps: GridCellProps) => {
    const { dataItem, field } = cellProps;
    const column = this.findColumn(field);
    const { cellType, dataIndex, editable } = column;

    let valueItem;
    if (field) {
      valueItem = _.get(dataItem, field);
    }

    if (editable && _.isFunction(this.props.customCellRender)) {
      return this.props.customCellRender(cellProps, column, valueItem);
    }

    let cellRenderKey = _.isUndefined(cellType) ? dataIndex.split('.').shift() : cellProps.dataItem?.rowConfig?.[field]?.cellType ? cellProps.dataItem?.rowConfig?.[field].cellType : cellType;

    if (this.CELL_RENDER_MAP.hasOwnProperty(cellRenderKey)) {
      const GridCell = this.CELL_RENDER_MAP[cellRenderKey];
      let additionalProps = (cellRenderKey === 'checkbox') ? {
        updateRecord: this.props.updateRecord ? this.props.updateRecord : this.updateRecord,
        onCellCheckboxChange: this.onCellCheckboxChange
      } : {};

      const cellConfig = _.isUndefined(column?.cellConfig) ? {} : _.cloneDeep(column.cellConfig);

      additionalProps['onRowDoubleClick'] = this.rowDoubleClick;
      additionalProps['onClick'] = this.rowClick;
      if (this.props.onExpandDetailButtonChange) {
        additionalProps['onExpandDetailButtonChange'] = this.props.onExpandDetailButtonChange;
      }
      if (this.props.onExpandTextButtonChange) {
        additionalProps['onExpandTextButtonChange'] = this.props.onExpandTextButtonChange;
      }
      if (this.props.hasExpandDetailButton) {
        additionalProps['hasExpandDetailButton'] = this.props.hasExpandDetailButton;
      }
      return (
        <GridCell
          {...cellProps}
          {...additionalProps}
          {...cellProps.dataItem?.rowConfig?.[field]}
          columnDataIndex={column.dataIndex}
          cellConfig={cellConfig}
          uniqueField={this.props.uniqueField}
        />
      );
    }

    if (_.isUndefined(valueItem)) {
      return this.loadingCell(cellProps);
    }

    return (
      <td
        aria-colindex={cellProps.ariaColumnIndex}
        className={cellProps.className}
        {...cellProps.dataItem?.rowConfig?.[field]}
      >
        {valueItem}
      </td>
    );
  };

  private headerCellRender = (props: any) => {
    const column = this.findColumn(props.field);
    const { columnMenuConfig, customHeaderRenderer } = column;
    const sortable = columnMenuConfig && columnMenuConfig.sortable !== false;
    let returnValue = <span />;

    if (column) {
      const {
        headerCellConfig,
        headerCellType,
        headerCustomContent
      } = column;

      if (customHeaderRenderer) {
        return customHeaderRenderer(props);
      } else if (this.HEADER_CELL_RENDER_MAP.hasOwnProperty(headerCellType)) {
        const GridHeaderCell = this.HEADER_CELL_RENDER_MAP[headerCellType];
        let disabled;
        let checked;
        if (headerCellType === 'checkbox') {
          checked = this.isHeaderCheckboxSelected();
          disabled = true;
          this.state.data.every((item) => {
            if (!item.disabled) {
              disabled = false;
              return false;
            } else {
              return true;
            }
          });
        }
        returnValue = (
          <GridHeaderCell
            {...props}
            headerCellConfig={headerCellConfig}
            headerCellType={headerCellType}
            headerCustomContent={headerCustomContent}
            sortable={sortable}
            onHeaderChange={this.onHeaderChange}
            checked={checked}
            disabled={disabled}
          />
        );
      } else {
        returnValue = (
          <HeaderCell
            {...props}
            headerCellConfig={headerCellConfig}
            headerCustomContent={headerCustomContent}
            sortable={sortable}
          />
        );
      }
    }

    return returnValue;
  };

  private renderButton: Function = (cellProps: GridCellProps) => {
    const { dataItem, field } = cellProps;
    let valueItem;
    if (field) {
      valueItem = _.get(dataItem, field);
    }
    if (_.isUndefined(valueItem)) {
      return this.loadingCell(cellProps);
    }

    let content = (
      <Button
        fillMode={'flat'}
        themeColor={'primary'}
        tabIndex={0}
      >
        {valueItem}
      </Button>
    );
    return (
      <td aria-colindex={cellProps.ariaColumnIndex}>
        {content}
      </td>
    );
  };

  private renderColumnMenu = ({ column, dynamicTranslationForSort, onCloseMenu, withResetSort }): any => {
    let { field } = column;
    const { columnMenuConfig = {} } = this.findColumn(field);
    field = columnMenuConfig.mappingFilterName ?? field;
    let filter;
    if (_.isArray(field)) {
      filter = this.state.filterData[[...field].shift()];
    } else {
      filter = this.state.filterData[field];
    }
    const { items: data, values } = filter || {};

    const isSorted = this.state.sort.some(sort => sort?.field === field);

    return (
      <ColumnMenu
        closeMenu={onCloseMenu}
        column={column}
        data={data}
        onFilterChange={this.onFilterChange}
        onSortChange={this.onSortChange}
        values={values}
        withResetSort={withResetSort}
        dynamicTranslationForSort={dynamicTranslationForSort}
        isSorted={isSorted}
        {...columnMenuConfig}
      />
    );
  };

  /**
   * Returns column configuration depending on dataIndex and filter configuration for given filed
   */
  createGridColumn(config) {
    const { nonResizableColumns = [], nonReordeableColumns = [] } = this.props;
    const {
      cellRender,
      columnMenu,
      dataIndex,
      header,
      headerAlign,
      headerCell,
      locked = false,
      minResizableWidth = 100,
      orderIndex,
      sortable = false,
      width,
      className,
      headerSelectionValue
    } = config;
    let {
      headerClassName
    } = config;

    const alignCls = headerAlign ? this.headerContentAlign[headerAlign] : '';

    headerClassName = classnames({
      [headerClassName]: !!headerClassName
    }, {
      [alignCls]: !!alignCls
    });

    const columnProps: any = {
      cell: cellRender,
      columnMenu,
      field: dataIndex,
      filterable: false,
      headerCell,
      headerClassName,
      id: dataIndex,
      key: dataIndex,
      className: className,
      locked,
      minResizableWidth,
      orderIndex,
      reorderable: !nonReordeableColumns.includes(dataIndex),
      resizable: !nonResizableColumns.includes(dataIndex),
      title: header,
      width,
      headerSelectionValue
    };

    if (this.props.sortable && sortable) {
      Object.assign(columnProps, {
        headerClassName: classnames({
          [headerClassName]: !!headerClassName
        }, 'tp-sortable'),
        className: classnames({
          [className]: !!className
        }, 'tp-sortable')
      });
    }

    return <GridColumn {...columnProps} />;
  }

  /**
   * Function for geting information if header checkbox is selected
   */
  isHeaderCheckboxSelected = (): boolean => {
    const selectedStateLength = Object.keys(this.state.selectedState).length;
    if (this.state.data.length && this.state.data.length === selectedStateLength) {
      return this.state.data.findIndex(
        item => !this.state.selectedState[this.getIdGetter()(item)]
      ) === -1;
    }
    return false;
  }

  groupColumnMapMethod = (columnConfig: any, index: number): JSX.Element => {
    const dataIndexExpandedColumn = `${columnConfig.subColumns[1].dataIndex}-expanded`;
    const expandedColumn = columnConfig.subColumns.find(column => column?.dataIndex === dataIndexExpandedColumn);
    if (!expandedColumn) {
      columnConfig.subColumns.unshift({
        header: ' ',
        dataIndex: `${columnConfig.subColumns[0].dataIndex}-expanded`,
        cellType: 'expandDetails',
        width: 24,
        className: 'tp-cell-expand-details',
        headerClassName: 'tp-cell-expand-details-header'
      });
    }

    const visibleSubColumns = columnConfig.subColumns.filter(this.isColumnVisible);
    const renderedSubColumns = visibleSubColumns.map((subColumn, _subIndex) => this.columnsMapMethod(subColumn, index));
    return (
      <GridColumn
        {...columnConfig}
        headerCell={columnConfig.headerRenderer}
      >
        {renderedSubColumns.length === 0 ? <></> : renderedSubColumns}
      </GridColumn>
    );
  }

  /**
   * Mapping method for grid config
   */
  columnsMapMethod = (column: any, index: number): JSX.Element => {
    let config: any;
    if (_.isFunction(this.props.columnsMapMethod)) {
      return this.props.columnsMapMethod(column, index);
    } else {
      const { cellType, columnMenuConfig, headerAlign, headerClassName, headerCellType, className } = column;
      let selectedAllCheckedProps = {};
      if (cellType === 'checkbox' && headerCellType === 'checkbox') {
        selectedAllCheckedProps = {
          headerSelectionValue: this.isHeaderCheckboxSelected()
        };
      }
      let dataIndex = column.dataIndex;
      let rendererKey = _.isUndefined(cellType) ? dataIndex.split('.').shift() : cellType;
      let header = column.header;
      let headerCell = this.headerCellRender;
      let cellRender = this.cellRender;
      let columnMenu;
      let { CELL_RENDER_MAP = undefined } = this.props;
      if (CELL_RENDER_MAP && CELL_RENDER_MAP.hasOwnProperty(rendererKey)) {
        cellRender = CELL_RENDER_MAP[rendererKey];
      }
      let orderIndex = column.orderIndex || index;

      if (columnMenuConfig) {
        columnMenu = this.renderColumnMenu;
      }

      config = this.createGridColumn({
        cellRender,
        columnMenu,
        dataIndex,
        header,
        headerAlign,
        headerCell,
        headerClassName,
        className,
        locked: column.locked,
        minResizableWidth: column.minResizableWidth,
        orderIndex,
        sortable: column.sortable,
        width: column.width,
        ...selectedAllCheckedProps
      });
      return config;
    }
  };

  /**
   * Return loading cell if data is not populated
   */
  loadingCell = (cellProps: GridCellProps) => {
    const { uniqueField } = this.props;
    let result = null;
    // added hasOwnProperty so that we can differentiate undefined from not existing properties
    if (uniqueField && cellProps.dataItem.hasOwnProperty(uniqueField) && cellProps.dataItem[uniqueField] === undefined) {
      result = (
        <td aria-colindex={cellProps.ariaColumnIndex}>
          <Skeleton />
        </td>
      );
    }
    return result;
  };

  /**
   * Prepare params for grid action request
   */
  prepareRequestParams = (pageNumber: number) => {
    const {
      pageNumberName = 'pageNumber',
      pageSize,
      pageSizeName = 'pageSize',
      requestConfig,
      requestTreeListConfig
    } = this.props;
    const { sort } = this.state;
    const requestParams: any = {};

    const params = {
      ...((requestConfig && requestConfig?.params) || {}),
      ...(this.requestConfig?.params || {})
    };

    Object.assign(requestParams, {
      [pageNumberName]: pageNumber,
      [pageSizeName]: pageSize
    }, params, this.getFilterData(true));

    if (!_.isEmpty(sort)) {
      const sorter = sort[0];
      requestParams.sort = `${sorter.fieldMapName ?? sorter.field},${sorter.dir}`;
    }

    /**
     * This block of code is used to check if there are no search parameters or they are empty
     * and set the `forceExpanded` property of `NavigationBaseTreeGrid` to false to not expand the tree lists
     */
    if (this.props.gridType === 'tree' && requestTreeListConfig?.treeListRequestParamsMap?.subRowIdMap) {
      let cloneRequestParams = { ...requestParams };
      cloneRequestParams = _.omit(cloneRequestParams, [pageNumberName, pageSizeName, 'sort', 'dispatcherId']);
      if (!_.isEmpty(cloneRequestParams)) {
        Object.keys(cloneRequestParams).forEach((propName) => {
          if (_.isEmpty(cloneRequestParams[propName])) {
            delete cloneRequestParams[propName];
          }
        });
      }
      if (_.isEmpty(cloneRequestParams)) {
        this.props.setForceExpanded(false);
      } else {
        this.props.setForceExpanded(true);
      }
    }

    return requestParams;
  };

  forceFocusLastSelectedIndexRow = () => {
    if (document.activeElement?.nodeName.toLowerCase() === 'body') {
      this.focusLastSelectedIndexRowSync();
    }
  };

  /**
   * Set new data and selectedState in state
   * @param {any[]} data
   */
  loadData = (data: any[]) => {
    data = this.getDisabledAllCheckboxData(data).data;
    if (this.props.selectableAll) {
      const selectedState = {};
      data.forEach((item) => {
        selectedState[this.getIdGetter()(item)] = item[this.props.selectableAll];
      });
      this.setState({ data, selectedState });
    } else {
      this.setState({ data });
    }
  };

  /**
   * Updates aria-expanded role if row is selected and if row is expanded, than calls `onRowControls` method from props.
   * @param ref
   */
  private updateAriaRole = (ref: any) => {
    if (ref) {
      let uniqueField: string = '';
      const { data } = this.state;
      if (this.lastClickedIndex !== -1 && data.length) {
        const dataItem: any = _.get(data, this.lastClickedIndex);
        if (dataItem) {
          uniqueField = dataItem[this.props.uniqueField];
        }
      }
      const ariaControls: string = ref.getAttribute('aria-controls');
      let expanded: boolean = ref.id === uniqueField && !!document.getElementById(ariaControls);

      // Keep the scroll position sync with the selected row
      // ref.scrollIntoView({ block: 'nearest' });
      if (expanded && _.isFunction(this.props.onRowControls)) {
        this.props.onRowControls(ref);
      }
    }
  };

  /**
   * Setter for lastSelectedIndex.
   * @param dataItem
   * @param subDataItem
   * @param isClicked If isClicked is `true`, then lastClickedIndex will be changed.
   */
  public setLastSelectedIndex = ({ dataItem, subDataItem }: any, isClicked: boolean = false) => {
    const lastSelectedIndex: number = this.getLastSelectedIndexByDataItem(dataItem);
    const sendObj = _.isObject(subDataItem) ? subDataItem : dataItem;
    this.setSelectedRowDataItem(_.isObject(sendObj) ? {
      ...sendObj
    } : null);
    if (isClicked) {
      this.lastClickedIndex = lastSelectedIndex;
    }
    if (this.props.accessibilityRowDescription) {
      this.setState({
        activeRowIndex: this.lastSelectedIndex
      });
    }
  };

  /**
   * Set EnableNavigation
   * @param {boolean} enableNavigation
   */
  public setEnableNavigation = (enableNavigation: boolean): void => {
    this.enableNavigation = enableNavigation;
  }

  /**
   * Returns 'lastSelectedIndex' property by dataItem
   * @param {any} dataItem
   */
  getLastSelectedIndexByDataItem = (dataItem: any) => {
    let dataItemCopy = _.cloneDeep(dataItem);
    delete dataItemCopy?.selected;
    this.setLastSelectedIndexValue(_.findIndex(this.state.data, dataItemCopy));
    return this.lastSelectedIndex;
  }

  /**
   * Used for preventing selectionChange. Return false to prevent selection.
   * @param dataItem
   * @param options
   */
  private beforeSelectionChange = (dataItem: any, options: any): boolean => {
    let allowSelectionChange: boolean = true;
    if (_.isFunction(this.props.beforeSelectionChange)) {
      allowSelectionChange = !!this.props.beforeSelectionChange(dataItem, options);
    }
    return allowSelectionChange;
  };

  /**
   * Select first element in table if there are rows.
   */
  private selectFirst = () => {
    const { data } = this.state;
    if (data.length) {
      this.updateSelectionConfirmed(data[0], {});
    }
  };

  /**
   * Updates selection selection is allowed.
   */
  private updateSelection = (dataItem: any, options: any) => {
    let allowSelection: boolean = this.beforeSelectionChange(dataItem, options);
    if (allowSelection) {
      this.updateSelectionConfirmed(dataItem, options);
    }
  };

  /**
   * Handler for updating selection. Invokes `handleSelection` method passed trough props with dataItem, opetions
   * and newState.
   * @param dataItem
   * @param options
   */
  private updateSelectionConfirmed = (dataItem: any, options: any) => {
    const { uniqueField } = this.props;
    const { data = [], tasks } = this.state;
    const { doubleClick = false, shiftKey, ctrlKey, keyboard, contextMenuOptions, focus = true } = options;
    let firstSelectedIndex: number = this.lastClickedIndex;

    if (keyboard) {
      this.scrollToRecord(dataItem);
    }

    let currentIndex = _.findIndex(data, {
      [uniqueField]: dataItem[uniqueField]
    });

    if (currentIndex === -1 && doubleClick) {
      // selecting already selected record, skip this
      // case when we double click record, and then press SPACE or ENTER
      currentIndex = _.findIndex(data as any[], {
        [uniqueField]: dataItem[uniqueField]
      });
    }

    let lastSelectedIndex = currentIndex;
    if (!shiftKey) {
      firstSelectedIndex = currentIndex;
    }

    if (!ctrlKey && Array.isArray(data)) {
      data.forEach((item: any) => {
        if (!_.isEmpty(item)) {
          item.selected = false;
        }
      });
      this.selectedData = [];
    }

    const select = doubleClick || contextMenuOptions ? true : !dataItem?.selected;

    let startIndex = Math.min(firstSelectedIndex, currentIndex);
    let endIndex = Math.max(firstSelectedIndex, currentIndex);

    let newSelectedData: any[] = [];
    let current: any;
    while (startIndex <= endIndex) {
      current = data[startIndex];
      if (!_.isEmpty(current)) {
        current.selected = select;
      }

      if (select || (!select && doubleClick)) {
        newSelectedData.push(current);
      } else {
        let selectedLength = _.get(this.selectedData, 'length');
        while (selectedLength) {
          selectedLength--;
          if (_.findIndex([this.selectedData[selectedLength]], current) !== -1) {
            this.selectedData.splice(selectedLength, 1);
            break;
          }
        }
      }
      startIndex++;
    }

    this.selectedData = this.selectedData.concat(newSelectedData);
    let newState: any = {
      data,
      lastSelectedIndex,
      selectedData: this.selectedData,
      firstSelectedIndex
    };
    if (contextMenuOptions?.offset) {
      this.setState({
        offset: contextMenuOptions.offset,
        showContextMenu: true,
        contextMenuItem: dataItem
      });
    }

    if (tasks) {
      Object.assign(newState, {
        tasks
      });
    }

    if (dataItem && _.isFunction(this.props.handleSelection)) {
      // handleSelection must be passed
      const noFocus = this.props.handleSelection(dataItem, options, newState);
      if (!noFocus) {
        this.setLastSelectedIndexValue(lastSelectedIndex);
        this.setLastSelectedIndex({ dataItem }, true);
        focus && this.focusLastSelectedIndexRow(true);
        this.setState({
          data
        });
      }
    }
    this.previousAction = 'select';
  };

  /**
   * Sets focus to row
   * @param {KeyboardEvent} e
   * @param {Boolean} enableNavigation
   * @param {any} dataItem
   * @param {boolean} isSetNextRow
   */
  public setRowSelect = (e: KeyboardEvent, enableNavigation: boolean, dataItem?: any, isSetNextRow?: boolean) => {
    const { keyCode, ctrlKey, shiftKey } = e;
    this.enableNavigation = enableNavigation;
    if ([Keys.up, Keys.down].includes(keyCode)) {
      e.preventDefault();
      if (!this.holdingArrowKeys) {
        this.holdingArrowKeys = true;
        const { uniqueField } = this.props;
        const { data = [] } = this.state;
        let lastSelectedIndex = _.findIndex(data, {
          [uniqueField]: dataItem[uniqueField]
        });
        if (keyCode === Keys.up && lastSelectedIndex >= 0) {
          dataItem = data[lastSelectedIndex];
        } else if (keyCode === Keys.down && lastSelectedIndex < data.length - 1) {
          dataItem = data[lastSelectedIndex + 1];
        }
        if (dataItem) {
          this.updateSelection(dataItem, {
            shiftKey,
            ctrlKey,
            keyboard: true
          });
          this.setLastSelectedIndex({ dataItem }, true);
          this.focusLastSelectedIndexRow(true);
        }
      }
    } else if (isSetNextRow) {
      if (this.isLastIndexOfLastRecord()) {
        this.setLastSelectedIndexValue(-1);
        this.enableNavigation = false;
      } else {
        const { data = [] } = this.state;
        const { uniqueField } = this.props;
        let lastSelectedIndex = _.findIndex(data, {
          [uniqueField]: dataItem[uniqueField]
        });
        if (lastSelectedIndex < data.length - 1) {
          dataItem = data[lastSelectedIndex + 1];
        } else {
          this.setLastSelectedIndexValue(-1);
        }
        if (dataItem) {
          this.setLastSelectedIndex({ dataItem });
        }
      }
    }
  }

  /**
   * Row click handler, used for handling grid selection.
   * If `onRowClick` parameter is not passed trough props, than default behaviour will be to call updateSelection
   * method.
   */
  rowClick = (event: GridRowClickEvent) => {
    this.enableNavigation = true;
    this.setLastSelectedIndex(event, true);
    if (_.isFunction(this.props.onRowClick)) {
      this.props.onRowClick(event);
    } else {
      const { nativeEvent, dataItem } = event;
      const { shiftKey, ctrlKey } = nativeEvent;
      this.updateSelection(dataItem, {
        shiftKey,
        ctrlKey,
        eventObj: event
      });
    }
  };
  /**
   * Row double click handler, used for handling grid selection.
   * If `onRowDoubleClick` parameter is not passed trough props, than default behaviour will be to call updateSelection
   * method.
   */
  rowDoubleClick = (event: GridRowDoubleClickEvent) => {
    this.setLastSelectedIndex(event, true);
    if (_.isFunction(this.props.onRowDoubleClick)) {
      this.props.onRowDoubleClick(event);
    } else {
      const { dataItem } = event;
      this.updateSelection(dataItem, {
        doubleClick: true
      });
    }
  };

  /**
   * Delete row handler, used for setting new data and selectedState values in the state
   * @param { any } dataItem
   */
  deleteRow = (dataItem: any) => {
    const data = [...this.state.data];
    let selectedState = {};
    const updatedData = data.filter(item => this.getIdGetter()(item) !== this.getIdGetter()(dataItem)).map((item: any) => {
      selectedState[this.getIdGetter()(item)] = item.checkbox;
      return item;
    });
    this.setState({
      data: updatedData,
      selectedState
    });
    if (_.isFunction(this.props.onDeleteRow)) {
      this.props.onDeleteRow(dataItem, {
        data: updatedData,
        selectedState,
        deleteUniqueField: this.getIdGetter()(dataItem)
      });
    }
  }

  /**
   * Basic rowRender method which adds `id` parameter to row based on `uniqueField` from props, `tabIndex` to enable
   * navigation via keyboard, and reference to enable dynamical changing of aria roles.
   * After that will add additional config passed trough `rowRender` from props.
   * @param trElement
   * @param rowProps
   */
  rowRender = (trElement: ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) => {
    const { uniqueField, autoHeightCell, reorderingRows, rowMarked } = this.props;

    if (this.props.rowSkeletonEnabled && uniqueField && !rowProps.dataItem[uniqueField]) {
      const { children, ...props } = trElement.props;

      trElement = (
        <trElement.type {...props}>
          <RowSkeleton
            data={this.rowSkeletonConfig}
            htmlTag="td"
          />
        </trElement.type>
      );
    } else {
      const { dataItem } = rowProps;
      const { props: trElProps } = trElement;
      const elementProps = { ...trElProps };
      const autoHeightRowCls = autoHeightCell ? 'tp-source-target-grid-row' : '';
      if (trElProps?.className) {
        elementProps.className = `${trElProps.className}${dataItem?.expanded ? ' tp-row-expanded' : ''}`;
        elementProps.className += ` ${autoHeightRowCls}`;
      } else if (autoHeightRowCls) {
        elementProps.className = autoHeightRowCls;
      }

      if (rowMarked === dataItem[uniqueField]) {
        elementProps.className = `${elementProps.className} k-selected`;
      }

      const controlsOpening: any = this.props.onAnyKeyDown ? {
        'aria-haspopup': true
      } : {};
      const accessibilityRowDescription = this.props.accessibilityRowDescription ? {
        'aria-describedby': 'row-description'
      } : {};
      const trProps: any = {
        ...elementProps,
        tabIndex: 0,
        ...controlsOpening,
        ...accessibilityRowDescription,
        id: dataItem[uniqueField],
        ref: (ref: any) => this.updateAriaRole(ref)
      };

      if (Array.isArray(this.props.contextMenu)) {
        let onContextMenu: Function = (e: React.MouseEvent) => {
          e.preventDefault();
          this.handleContextMenuOpen(e, rowProps.dataItem);
        };

        Object.assign(trProps, { onContextMenu });
      }

      trElement = React.cloneElement(trElement, {
        ...trProps
      }, elementProps.children);
      if (this.props.rowRender) {
        trElement = this.props.rowRender(trElement, rowProps) as ReactElement<HTMLTableRowElement>;
      }
      if (reorderingRows) {
        trElement = (
          <DraggableRow
            elementProps={trElement.props}
            {...rowProps}
          />);
      }
    }

    return trElement;
  };

  /**
   * Open context menu and set offset
   */
  handleContextMenuOpen = (e: React.MouseEvent, dataItem: any) => {
    if (_.isFunction(this.props.beforeContextMenuOpen)) {
      this.props.beforeContextMenuOpen(e, dataItem);
    }

    this.updateSelection(dataItem, {
      contextMenuOptions: {
        offset: {
          left: e.clientX,
          top: e.clientY
        }
      }
    });
  };

  /**
   * Handles tab key down event
   * @param {e: KeyboardEvent} e
   */
  handleTabKeyDown = (e: KeyboardEvent) => {
    const { keyboardShortcuts, onRowSelected } = this.props;
    const { target, shiftKey } = e;
    if (keyboardShortcuts) {
      const nodeType: string = _.get(target, 'nodeName').toLowerCase();
      this.tabKeyEventProps.holding = true;
      this.tabKeyEventProps.shift = shiftKey;
      const focusedElement = target as any;
      const isPartOfNavigationGrid: boolean = this.isPartOfNavigationGrid(e);
      const isFocusedElementContainerChild = this.isFocusedElementContainerChild(focusedElement);
      const isFocusedElementLastSelectedRowChild = this.isFocusedElementContainerChild(focusedElement, this.getLastSelectedRow());
      const dataLength = this.getGridCount();
      if (isPartOfNavigationGrid && !([-1, dataLength - 1].includes(this.lastSelectedIndex) && !isFocusedElementContainerChild && !isFocusedElementLastSelectedRowChild)) {
        this.tabKeyEventProps.intoContainer = true;
        const { waitScrollNumber } = this.tabKeyEventProps;
        if (shiftKey) {
          if (!isFocusedElementLastSelectedRowChild && this.lastSelectedIndex > 0) {
            if (this.tabKeyEventProps.waitScrollCounter > waitScrollNumber) {
              this.handleTabKey(shiftKey, true);
              this.tabKeyEventProps.waitScrollCounter = 0;
            }
            this.focusLastSelectedIndexRowSync(null, false, false);
            this.tabKeyEventProps.waitScrollCounter++;
            e.preventDefault();
          } else {
            this.tabKeyEventProps.waitScrollCounter = 0;
            if (nodeType === 'tr') {
              if (this.lastSelectedIndex === 0) {
                this.setLastSelectedIndexValue(-1);
              } else {
                this.handleTabKey(shiftKey);
              }
            }
          }
        } else {
          const lastIndex = dataLength - 1;
          if (!isFocusedElementLastSelectedRowChild && this.lastSelectedIndex !== lastIndex) {
            if (this.tabKeyEventProps.waitScrollCounter > waitScrollNumber) {
              this.handleTabKey(shiftKey, true);
              this.tabKeyEventProps.waitScrollCounter = 0;
            }
            this.focusLastSelectedIndexRowSync(null, true);
            this.tabKeyEventProps.waitScrollCounter++;
            e.preventDefault();
          } else {
            this.tabKeyEventProps.waitScrollCounter = 0;
            const lastSelectedRow = this.getLastSelectedRow();
            const lastSelectedRowElements = getNodesFromContainerFromTypes(lastSelectedRow, FOCUSABLE_ELEMENTS_WITHIN_TR, [{
              name: 'tabindex',
              value: '0'
            }]);
            let lastSelectedElement = lastSelectedRow;
            if (!_.isEmpty(lastSelectedRowElements)) {
              lastSelectedElement = _.last(lastSelectedRowElements);
            }
            if (focusedElement === lastSelectedElement) {
              const { data } = this.state;
              const dataItem = data[this.lastSelectedIndex];
              const isExpanded = dataItem.expanded;
              if (isExpanded) {
                this.scrollToLeft();
              } else {
                if (this.lastSelectedIndex === lastIndex) {
                  this.setLastSelectedIndexValue(-1);
                } else {
                  this.scrollToLeft();
                  this.handleTabKey(shiftKey);
                }
              }
            }
          }
        }
      } else {
        this.tabKeyEventProps.intoContainer = false;
      }
    }
  };

  /**
   * Handler of tab key on keydown method.
   * @param {Boolean} shiftKey
   * @param {Boolean} noInc (No Increment) -> It doesn't add scroll increment but sets appropriately scroll top value
   */
  handleTabKey = (shiftKey: boolean = false, noInc: boolean = false) => {
    let dataItem: any;
    const { data = [] } = this.state;
    if (_.isArray(data)) {
      let lastSelectedIndex = this.lastSelectedIndex;
      if (lastSelectedIndex === -1 && shiftKey) {
        lastSelectedIndex = data.length - 1;
      }

      if (this.previousAction === 'select' && !shiftKey) {
        this.previousAction = null;
        // dataItem = data[lastSelectedIndex];
      }

      if (!dataItem) {
        if (noInc) {
          dataItem = data[lastSelectedIndex];
        } else {
          if (shiftKey && lastSelectedIndex > 0) {
            dataItem = data[lastSelectedIndex - 1]; // This is the item that has to be passed to the form
            if (dataItem?.expanded && _.isFunction(this.props.setLastSelectedListItemId)) {
              const { uniqueField, uniqueFieldDetail, setLastSelectedListItemId } = this.props;
              const { subItems } = dataItem;
              const lastSubItem = subItems[subItems.length - 1];
              setLastSelectedListItemId(dataItem[uniqueField], lastSubItem[uniqueFieldDetail]);
              this.enableNavigation = false;
            }
          } else if (!shiftKey && lastSelectedIndex < data.length - 1) {
            dataItem = data[lastSelectedIndex + 1]; // This is the item that has to be passed to the form
          }
        }
      }
      if (dataItem) {
        const isNoScrollCase = !shiftKey && this.lastSelectedIndex + 1 === data.length - 1;
        if (!isNoScrollCase) {
          this.scrollToRecord(dataItem, shiftKey, true, noInc);
        }
        this.setLastSelectedIndex({ dataItem });
      }
    }
  };

  /**
   * Getter for first `tr` element ih upper hierarchy.
   * @param target
   */
  getTrElement = (target: any) => {
    let trElement: any = target;
    while (trElement && _.get(trElement, 'nodeName')?.toLowerCase() !== 'tr') {
      trElement = _.get(trElement, 'parentElement');
    }
    return trElement;
  };

  /**
   * Returns true if element that is in focus is located in container. If container is undefined,
   * container will be grid container
   * @param {Element} focusedElement
   * @param {HTMLElement} container Grid container
   */
  isFocusedElementContainerChild = (focusedElement: Element, container: HTMLElement = null) => {
    if (!container) {
      const virtualScroll: any = this.gridRef.vs;
      container = virtualScroll.table;
    }
    let parentElement = focusedElement;
    if (focusedElement !== container) {
      while (parentElement && parentElement !== container) {
        parentElement = _.get(parentElement, 'parentElement');
      }
    }
    return _.eq(parentElement, container);
  };

  /**
   * Creates event with all necessary data.
   * @param e
   * @param data
   */
  createEventData = (e: KeyboardEvent, data: any[]) => {
    const { target } = e;
    const trElement = this.getTrElement(target);
    const uniqueField: string = _.get(trElement, 'id');
    const index = _.findIndex(data, {
      [this.props.uniqueField]: uniqueField
    });

    return _.merge(e, {
      syntheticEvent: undefined,
      dataItem: data[index] || {},
      target,
      nativeEvent: e
    }) as any;
  };

  /**
   * Returns true if keydown and keyup events should be prevented if they are not within scope of NavigationGrid.
   * @param event
   */
  preventEventFromExecutionIfNotWithinNavigationGrid = (event: KeyboardEvent): boolean => {
    let element: any = event.target;
    if (
      element &&
      (element.nodeName.toLowerCase() === 'button') &&
      (element.getAttribute('aria-haspopup') === 'true') &&
      (element.getAttribute('aria-expanded') === 'true')
    ) {
      element = null;
    }
    if (document.getElementById(this.props.id)) {
      while (element && (element as any)?.id !== this.props.id) {
        element = element.parentElement;
      }
    }
    return element === null;
  };

  /**
   * Table keydown handler.
   * Manages selection with arrowUp and arrowDown, doesn't manage if focused element is Input field.
   */
  handleKeyDown = (e: KeyboardEvent) => {
    const { keyboardShortcuts, isListItemFocused, onRowSelected } = this.props;
    const { data = [] } = this.state;
    const { altKey, ctrlKey, keyCode, target, shiftKey } = e;
    if (Keys.tab === keyCode) {
      if (isListItemFocused) {
        this.enableNavigation = !isListItemFocused(data[this.lastSelectedIndex]) && !isListItemFocused(data[this.lastSelectedIndex - 1]);
      }
      if (this.enableNavigation) {
        this.handleTabKeyDown(e);
      }
    }
    if (this.enableNavigation) {
      let isPrevented: boolean = !this.preventEventFromExecutionIfNotWithinNavigationGrid(e);
      if (keyboardShortcuts && isPrevented) {
        const { onAnyKeyDown } = this.props;
        let nodeType: string = _.get(target, 'nodeName').toLowerCase();
        let isOriginallyTrElement: boolean = nodeType === 'tr';
        let enabledInputs: boolean = nodeType === 'input' && this.enabledInputElementTypes.includes(_.get(target, 'type'));
        if ([Keys.up, Keys.down].includes(keyCode) && (nodeType !== 'input' || enabledInputs)) {
          e.preventDefault();
          if (!this.holdingArrowKeys) {
            this.holdingArrowKeys = true;
            let dataItem: any;
            let lastSelectedIndex: number = this.lastSelectedIndex;

            // Jumps to tree list or jump to the next row
            const currentDataItem = data[lastSelectedIndex];
            const currentPreviousDataItem = data[lastSelectedIndex - 1];
            if (currentDataItem?.expanded && keyCode === Keys.down && lastSelectedIndex <= data.length - 1) {
              this.enableNavigation = false;
              this.props.setFocusToTreeList(currentDataItem, 'first');
            } else if (currentPreviousDataItem?.expanded && keyCode === Keys.up && lastSelectedIndex > 0) {
              this.enableNavigation = false;
              this.props.setFocusToTreeList(currentPreviousDataItem, 'last');
            } else {
              if (!shiftKey || !altKey) {
                if (keyCode === Keys.up && lastSelectedIndex > 0) {
                  dataItem = data[lastSelectedIndex - 1]; // This is the item that has to be passed to the form
                } else if (keyCode === Keys.down && lastSelectedIndex < data.length - 1) {
                  dataItem = data[lastSelectedIndex + 1]; // This is the item that has to be passed to the form
                }
              }
              if (dataItem) {
                this.updateSelection(dataItem, {
                  shiftKey,
                  ctrlKey,
                  keyboard: true
                });
                this.setLastSelectedIndex({ dataItem }, true);
                this.focusLastSelectedIndexRow(true);
              }
            }
          }
        }

        if (!isOriginallyTrElement && (FOCUSABLE_ELEMENTS_WITHIN_TR.includes(nodeType) || isFocusedElementHasTabIndex())) {
          if (ctrlKey && altKey && e.key.toLowerCase() === 'o') {
            if (_.isFunction(onAnyKeyDown)) {
              const event: any = this.createEventData(e, data);
              onAnyKeyDown(event);
            }
          } else if ([Keys.pageDown, Keys.pageUp, Keys.home, Keys.end].includes(keyCode)) {
            let trElement: any = this.getTrElement(e.target);
            if (trElement) {
              nodeType = 'tr';
              _.merge(e, {
                target: trElement
              });
            }
          } else if (_.isFunction(onAnyKeyDown)) {
            const event: any = this.createEventData(e, data);
            onAnyKeyDown(event);
          }
        }

        if (nodeType === 'tr') {
          switch (keyCode) {
            // case 79:
            //   if (altKey && ctrlKey) {
            //     e.preventDefault();
            //     let event: any = this.createEventData(e, data);
            //     event.dataItem.selected = false;
            //     this.rowDoubleClick(event);
            //   }
            //   break;
            case Keys.pageDown:
              e.preventDefault();
              this.focusElementWithinRow = SELECT_WITHIN_ROW.none;
              this.handlePageDown(e, data, shiftKey, true, false);
              this.isPageDownOrPageUpInProgress = true;
              onRowSelected?.(this.state.data[this.lastSelectedIndex], false);
              break;
            case Keys.end:
              e.preventDefault();
              if (ctrlKey) {
                this.focusElementWithinRow = SELECT_WITHIN_ROW.last;
                this.handlePageDown(e, data, shiftKey, false, false);
              }
              this.isKeyEndOrKeyHomeInProgress = true;
              break;
            case Keys.pageUp:
              e.preventDefault();
              this.handlePageUp(e, data, shiftKey);
              this.isPageDownOrPageUpInProgress = true;
              onRowSelected?.(this.state.data[this.lastSelectedIndex], false);
              break;
            case Keys.home:
              e.preventDefault();
              if (ctrlKey) {
                this.focusElementWithinRow = SELECT_WITHIN_ROW.first;
                this.handlePageUp(e, data, shiftKey, false);
              }
              this.isKeyEndOrKeyHomeInProgress = true;
              break;
            case Keys.enter:
              if (_.isFunction(this.props.onRowDoubleClick)) {
                const dataItem = data[this.lastSelectedIndex];
                this.props.onRowDoubleClick({ dataItem });
              }
              break;
            default:
              if (_.isFunction(onAnyKeyDown)) {
                const event: any = this.createEventData(e, data);
                onAnyKeyDown(event);
              }
              break;
          }
        } else if (nodeType === 'a' && keyCode === Keys.enter) {
          // @ts-ignore
          target.click?.();
        }
      }
    }
  };

  private getLastSelectedRow = (lastSelectedIndex: number = -1) => {
    if (lastSelectedIndex === -1) {
      lastSelectedIndex = this.lastSelectedIndex;
    }
    return this.getRowElementByIndex(lastSelectedIndex);
  };

  /**
   * Gets row element by row index.
   * @param index
   */
  getRowElementByIndex = (index: number) => {
    const { data = [] } = this.state;
    const dom = this.getDomNode(this.gridRef);
    const table = getNodesFromContainerFromType(dom, 'table')[1];
    let rows: HTMLTableRowElement[] = getNodesFromContainerFromType(table, 'tr');
    let trueRow = false;
    let lastSelectedDataItem = _.get(data[index], this.props.uniqueField);
    if (_.isNumber(lastSelectedDataItem)) {
      lastSelectedDataItem = lastSelectedDataItem.toString();
    }
    return rows.find((rowElement: HTMLTableRowElement) => {
      trueRow = rowElement.getAttribute('id') === lastSelectedDataItem;
      return trueRow;
    });
  };

  /**
   * When focus is on `button`, `a` or `input` than PAGE_UP and PAGE_DOWN wont work because row has to be in focus
   * in order to scroll to top or bottom of the table.
   * @param forceRowSelection If force selection is `true` then focus will be on row element even if it contains
   * `button`, `a` or `input`.
   * @param selectFirst
   */
  focusLastSelectedIndexRow = (forceRowSelection: boolean = false, selectFirst: boolean = true) => {
    let lastSelectedIndex = this.lastSelectedIndex;
    if (lastSelectedIndex === -1) {
      let selectedData = this.selectedData;
      if (selectedData.length) {
        lastSelectedIndex = selectedData[0].Index;
      }
    }
    const row = this.getRowElementByIndex(lastSelectedIndex);
    if (row) {
      setTimeout(() => {
        if (!this.state.showContextMenu) {
          this.focusLastSelectedIndexRowSync(row, forceRowSelection, selectFirst);
          this.focusElementWithinRow = SELECT_WITHIN_ROW.none;
        }
      }, 1);
    }
  };

  private focusLastSelectedIndexRowSync = (row: any = null, forceRowSelection: boolean = false, selectFirst: boolean = true) => {
    if (!row) {
      row = this.getLastSelectedRow();
    }
    if (row) {
      if (this.focusElementWithinRow !== SELECT_WITHIN_ROW.none) {
        selectFirst = this.focusElementWithinRow === SELECT_WITHIN_ROW.first;
        if (!selectFirst) {
          forceRowSelection = false;
        }
      }

      if (!forceRowSelection) {
        const elements = getNodesFromContainerFromTypes(row, FOCUSABLE_ELEMENTS_WITHIN_TR, [{
          name: 'tabindex',
          value: '0'
        }]);
        if (!_.isEmpty(elements)) {
          if (selectFirst) {
            row = _.head(elements);
          } else {
            row = _.last(elements);
          }
        }
      }
    }
    if (row) {
      row.focus();
    }
    return row;
  };

  /**
   * Returns true if element that is in focus is table raw of first record.
   * @param {Element} focusedElement
   */
  isFocusedElementFirstRecord = (focusedElement: Element): boolean => {
    const nodeType: string = _.get(focusedElement, 'nodeName').toLowerCase();
    const isOriginallyTrElement: boolean = nodeType === 'tr';
    const { data = [] } = this.state;
    const { uniqueField } = this.props;
    return isOriginallyTrElement && this.lastSelectedIndex !== 0 && focusedElement.id === data[0][uniqueField];
  }

  /**
   * Returns true if element that is in focus is table raw of last child of last record.
   * @param {Element} focusedElement
   */
  isFocusedElementLastChildOfLastRecord = (focusedElement: Element): boolean => {
    const nodeType: string = _.get(focusedElement, 'nodeName').toLowerCase();
    const isOriginallyTrElement: boolean = nodeType === 'tr';
    const { data } = this.state;
    const { uniqueField } = this.props;
    const lastRecord = data[data.length - 1];
    const ifOfLastRecord = lastRecord[uniqueField];
    if (!isOriginallyTrElement && !lastRecord.expanded && !!ifOfLastRecord) {
      let parentElement = _.get(focusedElement, 'parentElement');
      while (_.get(parentElement, 'nodeName').toLowerCase() !== 'tr') {
        parentElement = _.get(parentElement, 'parentElement');
      }
      let lastElementChild = getNodesFromContainerFromTypes(parentElement, FOCUSABLE_ELEMENTS_WITHIN_TR, [{
        name: 'tabindex',
        value: '0'
      }]);

      if (!_.isEmpty(lastElementChild)) {
        lastElementChild = _.last(lastElementChild);
      }
      return _.eq(focusedElement, lastElementChild) && ifOfLastRecord === parentElement.id;
    } else {
      return false;
    }
  }

  /**
   * Table keyup handler.
   * @param e
   */
  handleKeyUp = (e: KeyboardEvent) => {
    const { keyboardShortcuts, onRowSelected } = this.props;
    const { keyCode, ctrlKey } = e;
    if (keyboardShortcuts) {
      // if (this.preventEventFromExecutionIfNotWithinNavigationGrid(e) && keyCode !== Keys.tab) {
      //   return;
      // }
      switch (keyCode) {
        case Keys.tab:
          this.tabKeyEventProps.holding = false;
          if (this.enableNavigation) {
            const focusedElement = document.activeElement;
            const nodeType: string = _.get(focusedElement, 'nodeName').toLowerCase();
            const isFocusedElementContainerChild = this.isFocusedElementContainerChild(focusedElement);
            const isPartOfNavigationGrid: boolean = this.isPartOfNavigationGrid(e);
            if (isPartOfNavigationGrid) {
              if (this.tabKeyEventProps.shift) {
                if (!isFocusedElementContainerChild && this.lastSelectedIndex !== 0) {
                  this.focusLastSelectedIndexRowSync(null, false, false);
                } else if (this.lastSelectedIndex === -1 && this.isFocusedElementLastChildOfLastRecord(focusedElement)) {
                  const dataLength = this.getGridCount() - 1;
                  this.setLastSelectedIndexValue(dataLength);
                } else if (nodeType === 'tr') {
                  onRowSelected?.(this.state.data[this.lastSelectedIndex], false);
                }
              } else {
                const { data } = this.state;
                const lastIndex = _.isArray(data) ? data.length - 1 : -1;
                if (!isFocusedElementContainerChild && this.lastSelectedIndex !== lastIndex) {
                  this.focusLastSelectedIndexRowSync(null, true);
                } else if (isFocusedElementContainerChild && this.isFocusedElementFirstRecord(focusedElement)) {
                  this.scrollToLeft();
                  const dataItem = data[0];
                  this.setLastSelectedIndex({ dataItem });
                  this.updateSelection(dataItem, {
                    shiftKey: false,
                    ctrlKey,
                    keyboard: true
                  });
                } else if (nodeType === 'tr') {
                  onRowSelected?.(this.state.data[this.lastSelectedIndex], false);
                }
              }
            }
          }
          break;
        case Keys.up:
        case Keys.down:
          this.holdingArrowKeys = false;
          if (this.enableNavigation) {
            this.forceFocusLastSelectedIndexRow();
          }
          break;
        case Keys.pageUp:
        case Keys.pageDown:
          if (this.enableNavigation && this.isPageDownOrPageUpInProgress) {
            this.refocus.rowSelection = true;
            this.focusLastSelectedIndexRow(true);
            this.isPageDownOrPageUpInProgress = false;
          }
          break;
        case Keys.end:
        case Keys.home:
          if (this.isKeyEndOrKeyHomeInProgress) {
            this.focusElementWithinRow = Keys.home === keyCode ? SELECT_WITHIN_ROW.first : SELECT_WITHIN_ROW.last;
            this.focusLastSelectedIndexRow(false, Keys.home === keyCode);
            if (ctrlKey) {
              this.refocus = {
                isFirst: Keys.home === keyCode,
                rowSelection: false
              };
            }
            this.isKeyEndOrKeyHomeInProgress = false;
          }
          break;
        default:
          break;
      }
    }
  };

  /**
   * Handler of pressing PAGE_DOWN when focus is on table element.
   * It will call `onPageChange` method from props in order to get new chunk of data needed for last page.
   * Then table will be scrolled to its last row and that row will be selected.
   * @param e
   * @param data
   * @param shiftKey
   * @param forceRowSelection
   * @param selectFirst
   */
  private handlePageDown = (e: KeyboardEvent, data: any[], shiftKey: boolean, forceRowSelection: boolean = true, selectFirst: boolean = true) => {
    if (_.isFunction(this.props.onPageDownKey)) {
      this.props.onPageDownKey(e);
    }
    const lastEntry: any = _.last(data);
    this.scrollToRecord(lastEntry, shiftKey);
    this.setLastSelectedIndex({ dataItem: lastEntry });
    this.setLastSelectedIndexValue(data.length - 1);
    this.focusLastSelectedIndexRow(forceRowSelection, selectFirst);
  };

  /**
   * Handler of pressing PAGE_UP when focus is on table element.
   * It will call `onPageChange` method from props in order to get new chunk of data needed for first page.
   * Then table will be scrolled to its first row and that row will be selected.
   * @param e
   * @param data
   * @param shiftKey
   * @param forceRowSelection
   */
  private handlePageUp = (e: KeyboardEvent, data: any[], shiftKey: boolean, forceRowSelection: boolean = true) => {
    if (_.isFunction(this.props.onPageChange)) {
      this.props.onPageChange({
        page: {
          skip: 0
        }
      });
    }
    if (_.isFunction(this.props.onPageUpKey)) {
      this.props.onPageUpKey(e);
    }
    const firstEntry: any = _.head(data);
    this.scrollToRecord(firstEntry, shiftKey);
    this.setLastSelectedIndex({ dataItem: firstEntry });
    this.setLastSelectedIndexValue(0);
    this.focusLastSelectedIndexRow(forceRowSelection);
  };

  /**
   *  Scrolls container to dataItem if it is out of view, preserves horizontal scroll position.
   */
  private scrollToRecord = (dataItem: any, shiftKey: boolean = false, tabKey: boolean = false, noInc: boolean = false): boolean => {
    const virtualScroll: any = this.gridRef.vs;
    let scrolled = false;
    if (virtualScroll) {
      const { container } = virtualScroll;
      let rowHeights = virtualScroll?.rowHeightService?.heights || [this.props.rowHeight];
      const rowHeight = rowHeights[0];
      const { Index: rowIndex } = dataItem;
      scrolled = this.isRecordOutOfView(rowIndex, rowHeight, container, tabKey);
      const { holding, intoContainer } = this.tabKeyEventProps;
      const holdingIntoContainer = intoContainer && holding && noInc;
      if (scrolled || holdingIntoContainer) {
        const divisor = shiftKey ? tabKey ? 2 : 3 : 3;
        let scrollTop = rowHeight * rowIndex - container.clientHeight / divisor;
        if (noInc) {
          if (shiftKey) {
            scrollTop -= rowHeight;
          } else {
            scrollTop += rowHeight;
          }
        }
        const maxScrollTop = this.getMaxScrollTop();
        if (scrollTop > maxScrollTop) {
          scrollTop = maxScrollTop;
        } else if (scrollTop < 0) {
          scrollTop = 0;
        }
        container.scrollTo(container.scrollLeft, scrollTop);
      }
    }
    // <debug>
    if (!virtualScroll) {
      throw new Error('NavigationGrid expects "virtualScroll" property in order to work correctly!');
    }
    // </debug>
    return scrolled;
  };

  /**
   * Returns length of grid store
   */
  private getGridCount = () => {
    let count = 0;
    const { data } = this.state;
    if (_.isArray(data)) {
      count = data.length;
    }
    return count;
  };

  /**
   * Returns true if last index is the same as data length
   */
  public isLastIndexOfLastRecord = (): boolean => this.getGridCount() === this.lastSelectedIndex + 1;

  /**
   * Returns true if uniqueField from last record is the same as id
   * @param {string} id
   */
  public isLastRecord = (id: string): boolean => {
    const { data } = this.state;
    const { uniqueField } = this.props;
    const lastRecord = data[data.length - 1];
    return lastRecord[uniqueField] === id;
  }

  /**
   * Returns max scroll top value
   */
  private getMaxScrollTop = () => {
    let maxScrollTop = 0;
    const virtualScroll: any = this.gridRef.vs;
    if (virtualScroll) {
      const { container } = virtualScroll;
      let rowHeights = virtualScroll?.rowHeightService?.heights || [this.props.rowHeight];
      const rowHeight = rowHeights[0];
      const containerHeight = container.clientHeight || container.offsetHeight;
      const { data } = this.state;
      const numberOfRows = _.isArray(data) ? data.length : 0;
      if (numberOfRows) {
        maxScrollTop = rowHeight * numberOfRows - containerHeight;
      }
    }
    return maxScrollTop;
  };

  /**
   * Returns true if row is out of view
   * @param recordIndex 'Index' property of grid data item
   * @param rowHeight   Height of rows
   * @param scrollableContainer   Scrollable container of the grid, located inside the grid ref under 'vs.container' property
   * @param tabKey
   */
  private isRecordOutOfView = (recordIndex: number, rowHeight: number, scrollableContainer: any, tabKey: boolean = false) => {
    const { data = [] } = this.state;
    const numberOfSubItemsRecord = data[recordIndex] && data[recordIndex].expanded ? data[recordIndex].subItems?.length >= 5 ? 5 : data[recordIndex].subItems?.length : 0;
    const numberOfSubItemsRecordMinus = data[recordIndex - 1] && data[recordIndex - 1].expanded ? data[recordIndex - 1].subItems?.length >= 5 ? 5 : data[recordIndex - 1].subItems?.length : 0;
    const inc: number = tabKey ? 2 : 1;
    const decr: number = tabKey ? 1 : 0;
    const { scrollTop, offsetHeight } = scrollableContainer;
    return (scrollTop + offsetHeight < rowHeight * (recordIndex + inc + numberOfSubItemsRecord)) || (scrollTop > rowHeight * (recordIndex - decr - numberOfSubItemsRecordMinus));
  };

  private shouldPreventRecordDeselect = (e) => {
    let targetEl = e.target;
    let preventRecordSelectionChange = false;
    while (targetEl) {
      if (targetEl.getAttribute('data-allowrecorddeselect') || targetEl.classList.contains('data-allowrecorddeselect')) {
        break;
      }
      if (targetEl.getAttribute('data-preventrecorddeselect') || targetEl.classList.contains('data-preventrecorddeselect')) {
        preventRecordSelectionChange = true;
        break;
      }
      targetEl = targetEl.parentElement;
    }
    return preventRecordSelectionChange;
  }

  private handleClickBody = (e) => {
    const isFocusedElementContainerChild = this.isFocusedElementContainerChild(e.target);
    const shouldPreventRecordDeselect = this.shouldPreventRecordDeselect(e);
    if (!isFocusedElementContainerChild && !shouldPreventRecordDeselect) {
      this.tabKeyEventProps = { ...this.tabKeyInitEventProps };
      this.setLastSelectedIndexValue(-1);
    }
  };

  /**
   * Adds or removes keydown and keyup events from body based on isAdd property.
   * @param isAdd
   */
  handleKeyEvents = (isAdd: boolean = true) => {
    const method: string = isAdd ? 'addEventListener' : 'removeEventListener';

    document.body[method]('keydown', this.handleKeyDown, true);
    document.body[method]('keyup', this.handleKeyUp, true);
    document.body[method]('click', this.handleClickBody, true);
  };

  componentDidUpdate(prevProps: any, prevState: any, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    const { data, skip } = this.state;
    const { columns, loadExpandedData, rowHeight } = this.props;

    if (_.isBoolean(this.refocus.rowSelection) && this.lastSelectedIndex !== -1 && document.activeElement?.nodeName.toLowerCase() === 'body') {
      const { rowSelection, isFirst } = this.refocus;
      this.focusLastSelectedIndexRowSync(null, rowSelection, isFirst);
    }

    if (prevState.data.length !== data.length) {
      this.updateCaptionTag();
      if (this.isTotalChangedFromAnotherUser) {
        this.isTotalChangedFromAnotherUser = false;
        const { moduleId = this.moduleId } = this.props;
        this.dispatch('warningAnotherUserChangeTotalCount', {
          pos: Position.start
        }, {
          navigationGridModuleId: moduleId,
          navigationGridModuleName: this.navigationGridModuleName
        });
      }
    }

    // if is added new record from Navigation Base Editor Grid
    if (!!this.addedNewRecord) {
      this.scrollToRecord(this.addedNewRecord, false);
      this.addedNewRecord = undefined;
    }

    if (!_.isEqual(prevProps.columns, columns)) {
      this.setAccessibilityForGrid();
    }

    if (loadExpandedData && ((prevState.skip !== skip) || !_.isEqual(data, prevState.data))) {
      loadExpandedData();
    }

    const prevSkip = prevState.skip || 0;
    if (this.gridRef.vs?.table && this.props.autoHeightRowCorrection) {
      this.scrollCorrectionServiceVS.setCorrection(this.gridRef.vs, prevSkip, skip, data, rowHeight, this);
      this.isCollapsedAll = false;
      this.isExpandedAll = false;
    }
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    if ((this.props === nextProps) && (this.state === nextState)) {
      return false;
    }
    const isPropsDataEqual = _.isEqual(this.props.data, nextProps.data);
    const isPropsGridConfigEqual = _.isEqual(this.props.columns, nextProps.columns);
    const isPropsGridFilterDataEqual = _.isEqual(this.props.filterData, nextProps.filterData);
    let stateCallback;
    let stateObj: any = {};
    if (!isPropsDataEqual && nextProps.data) {
      stateObj = this.getDisabledAllCheckboxData([...nextProps.data]);
    }
    if (!isPropsGridConfigEqual) {
      stateObj.columns = [...nextProps.columns];
      stateCallback = this.updateRowSkeletonConfig;
    }
    if (!isPropsGridFilterDataEqual) {
      stateObj.filterData = { ...nextProps.filterData };
    }
    const isStateObjectEmpty = _.isEmpty(stateObj);
    if (!isStateObjectEmpty) {
      this.setState(stateObj, stateCallback);
    }
    return isStateObjectEmpty;
  };

  onRefreshData = (requestCfg?: RequestDataBase, additionalData: object = {}) => {
    let { requestActionName, moduleId = this.moduleId, requestConfig: propsRequestConfig } = this.props;

    if (_.isObject(propsRequestConfig)) {
      // if requestConfig is passed via props than it will use default action to refresh grid data
      requestActionName = 'refreshAnyNavigationGridData';
      _.merge(additionalData, propsRequestConfig);
    }
    this.dispatch(requestActionName, {
      pos: Position.start,
      dispatcherId: propsRequestConfig?.dispatcherId
    }, {
      ...additionalData,
      navigationGridModuleId: moduleId,
      navigationGridModuleName: this.navigationGridModuleName
    });
  };

  moduleDidMount() {
    super.moduleDidMount();
    this.refreshData();
  }

  componentDidMount() {
    super.componentDidMount();
    this.handleKeyEvents(true);
    this.updateRowSkeletonConfig();
    let element: any = this.getDomNode();
    if (element) {
      // TODO replace with div wrapper with id when core-ui is merged with dispatcher
      const { id = '' } = this.props;
      // <debug>
      if (!id) {
        throw new Error('NavigationGrid expects id property!');
      }
      // </debug>
      element.id = id;
      this.createCaptionTag(element);
    }
    this.addAttributeForPreventingDeselectionOnTable();
  }

  /**
   * Adding attribute for preventing row deselection on kendo table element
   */
  addAttributeForPreventingDeselectionOnTable = () => {
    this.gridRef?.tableBodyRef?.current?.setAttribute('data-preventrecorddeselect', 'true');
  };

  componentWillUnmount() {
    super.componentWillUnmount();
    this.handleKeyEvents(false);
  }

  private createCaptionTag = (element) => {
    const table = element.querySelector('table');
    const caption = document.createElement('caption');
    caption.setAttribute('class', 'sr-only');
    table.insertBefore(caption, table.firstChild);
    this.accessibilityCaptionElement = new QuerySelector(caption);
    this.updateCaptionTag();
  };

  private updateCaptionTag = () => {
    if (this.accessibilityCaptionElement.getDomElement()) {
      this.accessibilityCaptionElement.getDomElement().innerHTML = this.props.accessibilityCaption;
    }
  };

  private updateRowSkeletonConfig = () => {
    this.rowSkeletonConfig = this?.gridRef?._columns?.map?.(({ field }) => ({
      type: this.findColumn(field)?.cellType || 'text'
    }));
  };

  private isColumnVisible = (column: any) => !column.hidden;

  /**
   * Handles selection change of selectedField property.
   * @param event
   */
  private onSelectionChange = (event: GridSelectionChangeEvent) => {
    if (this.props.selectedField && _.isFunction(this.props.onSelectionChange)) {
      this.props.onSelectionChange(event);
    }
  };

  private onScroll = (event: GridEvent) => {
    if (_.isFunction(this.props.onScroll)) {
      this.props.onScroll(event);
    }
    this.tabKeyEventProps.changedScrollTop = true;
    const virtualScroll: any = this.gridRef.vs;
    if (virtualScroll) {
      const { holding, shift, intoContainer, currentScrollTop } = this.tabKeyEventProps;
      const holdingIntoContainer = intoContainer && holding;
      const scrollTop = virtualScroll.container.scrollTop;
      if (holdingIntoContainer && !shift && scrollTop < currentScrollTop) {
        this.handleTabKey(false, true);
      } else if (holdingIntoContainer && shift && scrollTop > currentScrollTop) {
        this.handleTabKey(true, true);
      } else {
        this.tabKeyEventProps.currentScrollTop = scrollTop;
      }
    }

    this.closeContextMenuOnScroll();
  };

  /**
   * Scroll grid to left side
   */
  scrollToLeft = () => {
    this.gridRef.vs?.container.scrollTo(0, 0);
  }

  private setGridReference = (el: any) => {
    if (el) {
      this.gridRef = el;
      if (this.props.autoHeightRowCorrection) {
        this.gridRef.getVirtualScroll = () => VirtualScrollFixed;
      }
    }
  };

  setGridData = (data: any, forceSkip?: boolean) => {
    this.props.setGridData(data, forceSkip);
  };

  setFilterData = (data: any) => {
    this.setState({
      filterData: data
    });
  };

  setStateWithAction = (state: any, action: string, callback: Function) => {
    this.setState(state, () => {
      this.dispatch(action, Position.end, callback());
    });
  }

  parseGridData = (data: any) => {
    if (_.isFunction(this.props.parseGridData)) {
      const allRemoteData = this.state.data.filter(item => _.isNumber(item.OriginalIndex) && item.populated);
      const { pageNumber, skip } = this.state;
      const { pageSize } = this.props;
      const prevPageData: any[] = allRemoteData.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
      const nextPageData: any[] = allRemoteData.slice((pageNumber + 1) * pageSize, (pageNumber + 2) * pageSize);
      data = this.props.parseGridData(data, this.props.insertRecords, {
        allRemoteData,
        prevPageData,
        nextPageData,
        pageNumber,
        skip,
        pageSize
      });
    }
    return data;
  };

  parseCellData = (data: any) => {
    if (_.isFunction(this.props.parseCellData)) {
      data = this.props.parseCellData(data);
    }
    return data;
  };

  getDataState = () => {
    const { data } = this.state;
    return [...data];
  };

  /**
   * Check if totalElements from get a response is different from total in state
   * @param {number} totalElements
   */
  public setIsTotalChanged = (totalElements: number): void => {
    const { total, dataLoadDone, addedTotal } = this.state;
    this.isTotalChangedFromAnotherUser = dataLoadDone && _.isNumber(totalElements) && (total !== totalElements + addedTotal);
  }

  getRecord = (search: any, searchBy: SearchRecordBy) => this.props.getRecord(search, searchBy);

  getRecordByIndex = (index: number) => this.getDataState()[index];

  getRecordIndex = (item: any) => this.props.getRecordIndex(item);

  getVisibleRecords = () => this.gridRef.props.data;

  /**
   * sets focusable headers
   */
  setAccessibilityForGrid = () => {
    const gridRef: any = this.getDomNode();
    const thList = gridRef.querySelectorAll('th');
    thList.forEach((th: any) => {
      th?.setAttribute('tabindex', '0');
    });
  };

  updateRecord = (item: any) => {
    if (this.props.updateRecord) {
      this.props.updateRecord(item);
    } else {
      item = {
        ...item,
        populated: true
      };
      const dataState = this.getDataState().slice();
      const { uniqueField } = this.props;
      const index = dataState.findIndex(record => record[uniqueField] === item[uniqueField]);
      dataState[index] = {
        ...dataState[index],
        ...item
      };
      this.setState({ data: dataState });
    }
  };

  updateRecords = (items: any[]) => {
    this.props.updateRecords(items);
  };

  updateRecordsByIndex = (items: any[], index: number) => {
    this.props.updateRecordsByIndex(items, index);
  };

  /**
   * Returns current scroll position
   */
  getScrollPosition = () => {
    if (_.isNumber(this?.gridRef?.vs?.lastScrollTop)) {
      return this.gridRef.vs.lastScrollTop;
    } else {
      return this?.gridRef?.vs?.container?.scrollTop;
    }
  };

  /**
   * Reload All Grid Data
   */
  reloadGridData = (params: object = {}, url = '', method: '', saveScroll: boolean = true) => {
    let scrollPosition = this.getScrollPosition();
    let { pageNumber, skip, selectedState } = this.state;
    const {
      moduleId = this.moduleId,
      requestConfig: requestConfigProps,
      selectableAll,
      resetSelectionOnReload = true
    } = this.props;
    if (!saveScroll) {
      pageNumber = 0;
      skip = 0;
      scrollPosition = 0;
    }
    if (selectableAll && resetSelectionOnReload) {
      selectedState = {};
    }
    this.setLastSelectedIndexValue(-1);
    this.lastClickedIndex = -1;
    this.setState({
      data: [],
      selectedState: selectedState,
      pageNumber,
      pageDataPopulated: [pageNumber],
      dataLoadDone: false,
      addedTotal: 0
    }, () => {
      this.dispatch('reloadAnyNavigationGridData', Position.start, {
        url: url || requestConfigProps.url,
        method: method || requestConfigProps.method,
        scrollRequestParams: params,
        pageNumber,
        scrollPosition,
        skip,
        navigationGridModuleId: moduleId,
        navigationGridModuleName: this.navigationGridModuleName
      });
    });
  };

  /**
   * Sync Reload All Grid Data
   * @param params
   * @param url
   * @param method
   * @param saveScroll
   */
  syncReloadGridData = async (params: object = {}, url = '', method: '', saveScroll: boolean = true) => {
    let scrollPosition = this.getScrollPosition();
    let { pageNumber, skip, selectedState } = this.state;
    const {
      moduleId = this.moduleId,
      requestConfig: requestConfigProps,
      selectableAll,
      resetSelectionOnReload = false
    } = this.props;
    if (!saveScroll) {
      pageNumber = 0;
      skip = 0;
      scrollPosition = 0;
    }
    if (selectableAll && resetSelectionOnReload) {
      selectedState = {};
    }
    this.setLastSelectedIndexValue(-1);
    this.lastClickedIndex = -1;

    await this.setStateSync({
      data: [],
      selectedState: selectedState,
      pageNumber,
      pageDataPopulated: [pageNumber],
      dataLoadDone: false,
      addedTotal: 0
    });
    return {
      url: url || requestConfigProps.url,
      method: method || requestConfigProps.method,
      scrollRequestParams: params,
      pageNumber,
      scrollPosition,
      skip,
      navigationGridModuleId: moduleId,
      navigationGridModuleName: this.navigationGridModuleName
    };
  };

  /**
   * Reload All Grid Data and Scroll to top
   */
  public reloadGridDataAndScrollTop = (params: object = {}, url = '', method: '') => {
    this.reloadGridData(params, url, method, false);
  }

  /**
   * Refreshes Grid Data
   */
  refreshGridData = (params: any = {}, url = '', method: '', headers?) => {
    let { moduleId = this.moduleId, requestConfig: requestConfigProps } = this.props;
    let { pageNumber = 0 } = this.state;
    if (!_.isEmpty(params?.dispatcherId)) {
      delete params.dispatcherId;
    }
    if (_.isEmpty(params)) {
      params = this.prepareRequestParams(pageNumber);
    } else if (!_.isUndefined(params.pageNumber)) {
      pageNumber = params.pageNumber;
    }
    this.setState({
      data: [],
      pageDataPopulated: [pageNumber]
    }, () => {
      this.dispatch('refreshAnyNavigationGridData', Position.start, {
        url: url || requestConfigProps.url,
        method: method || requestConfigProps.method,
        headers: headers || requestConfigProps.headers,
        scrollRequestParams: params,
        navigationGridModuleId: moduleId,
        navigationGridModuleName: this.navigationGridModuleName
      });
    });
  };

  requestData = (pageNumber: number = 0, skip: number = 0) => {
    this.props.requestData(pageNumber, skip);
  }

  /**
   * Sync request data from hoc component
   * @param pageNumber
   * @param skip
   */
  syncRequestData = async (pageNumber: number = 0, skip: number = 0) => {
    await this.props.syncRequestData(pageNumber, skip);
  }

  /**
   * Reset grid data and all paging params
   */
  resetGridData = () => {
    this.setState({
      data: [],
      pageNumber: 0,
      pageDataPopulated: [0],
      dataLoadDone: false,
      addedTotal: 0
    });
    this.setLastSelectedIndexValue(-1);
    this.lastClickedIndex = -1;
  };

  /**
   * Sync reset grid data and all paging params
   */
  syncResetGridData = async () => {
    const stateData = {
      data: [],
      pageNumber: 0,
      pageDataPopulated: [0],
      dataLoadDone: false,
      addedTotal: 0
    };
    await this.setStateSync(stateData);
    this.setLastSelectedIndexValue(-1);
    this.lastClickedIndex = -1;
  };

  /**
   * Reconfigure grid columns
   * @param {any[]} columns
   * @param {string} gridModuleId
   */
  reconfigureColumns = (columns, gridModuleId) => {
    if (this.moduleId === gridModuleId) {
      let state = {
        ...this.state,
        columns
      };
      this.setState(state);
    }
  };

  /**
   * Moves vertical scroll on grid to desired position
   * @param scrollPosition
   */
  scrollToLastPosition = (scrollPosition: number) => {
    this.gridRef.vs?.container.scrollTo(0, scrollPosition);
  };

  /**
   * Srcolls to row record
   * @param {any} record
   * @param {Boolean} withImageCorrection
   */
  scrollToRow = (record: any, withImageCorrection?: boolean) => {
    const vs: any = this.gridRef.vs;
    if (vs && this.props.getRecordIndex) {
      const index = this.props.getRecordIndex(record);
      if (withImageCorrection) {
        this.dispatch('scrollToRowWithImageCorrection', Position.end, {
          rowIndexToScrollImg: index
        });
      } else {
        this.scrollToRowIndex(index);
      }
    }
    // <debug>
    if (!(vs && this.props.getRecordIndex)) {
      throw new Error('NavigationGrid expects "virtualScroll" property in order to work correctly!');
    }
    // </debug>
  }
  /**
   * Scrolls to segment with row index (It uses in grid with generated images)
   * @param {Number} index Index of row where it needs to scroll
   * @param count
   */
  scrollToRowIndex = (index: number, count: number = 0) => {
    if (count === 0) {
      this.currentScrollToIndex = index;
    }
    let vs: any = this.gridRef.vs;
    if (vs && this.currentScrollToIndex === index) {
      index = this.currentScrollToIndex;
      const { data } = this.state;
      const { rowHeight } = this.props;
      const corr = this.scrollCorrectionServiceVS.setCorrection(this.gridRef.vs, undefined, undefined, data, rowHeight, this);
      const { container, rowHeightService, containerHeight } = vs;
      let offsets = rowHeightService.offsets;
      let scrollTop = offsets[index];
      if (scrollTop > containerHeight - container.offsetHeight && count === 0) {
        scrollTop = containerHeight - container.offsetHeight;
      }
      container.scrollTo(container.scrollLeft, scrollTop);
      if (!(count > 1 && (container.scrollTop === offsets[index] || corr))) {
        setTimeout(() => {
          this.scrollToRowIndex(index, ++count);
        }, 500);
      }
    }
    // <debug>
    if (!vs) {
      throw new Error('NavigationGrid expects "virtualScroll" property in order to work correctly!');
    }
    // </debug>
  }
  /**
   * Scrolls to segment with row index (It uses in grid with generated images)
   * @param {Number} index Index of row where it needs to scroll
   */
  scrollToRowIndexWithImageCorrection = (index: number) => {
    const vs: any = this.gridRef.vs;
    if (vs) {
      const { container, rowHeightService, containerHeight } = vs;
      const offsets = rowHeightService.offsets;
      const scrollTop = offsets[index];
      if (scrollTop > containerHeight - container.offsetHeight) {
        container.scrollTo(container.scrollLeft, containerHeight - container.offsetHeight);
        setTimeout(() => {
          container.scrollTo(container.scrollLeft, scrollTop);
        }, 500);
      } else {
        container.scrollTo(container.scrollLeft, scrollTop);
      }
    }
    // <debug>
    if (!vs) {
      throw new Error('NavigationGrid expects "virtualScroll" property in order to work correctly!');
    }
    // </debug>
  }
  /**
   * Sets correction to the table list position and
   * performs scrolling to the row index
   * until the segment with the row index is at the top of the scroll container
   * @param {Number} rowIndexToScroll Index of row where it needs to scroll
   */
  setCorrectionAfterImagesLoad = (rowIndexToScroll: number = -1) => {
    const rows: any[] = this.gridRef.vs?.table?.childNodes?.[1]?.childNodes;
    let firstRowIndex: string | number | null | undefined = rows?.[0]?.getAttribute('aria-rowindex') - 2;
    let lastRowIndex: string | number | null | undefined = rows?.[rows.length - 1]?.getAttribute('aria-rowindex') - 2;
    if (this.gridRef.vs?.table && this.props.autoHeightRowCorrection) {
      const { data } = this.state;
      const { rowHeight } = this.props;
      this.scrollCorrectionServiceVS.setCorrection(this.gridRef.vs, undefined, undefined, data, rowHeight, this);
      if (rowIndexToScroll !== -1) {
        this.scrollToRowIndexWithImageCorrection(rowIndexToScroll);
        const vs: any = this.gridRef.vs;
        if (vs) {
          const { container, rowHeightService, containerHeight } = vs;
          const offsets = rowHeightService.offsets;
          const scrollTop = container.scrollTop;
          const hScrollHeight = container.scrollWidth - container.offsetWidth > 0 ? 15 : 0;
          const isRowOnDown = offsets[rowIndexToScroll] > containerHeight - container.offsetHeight && scrollTop === containerHeight - container.offsetHeight;
          const isRowOnMiddle = scrollTop === offsets[rowIndexToScroll] && rowIndexToScroll >= firstRowIndex && rowIndexToScroll <= lastRowIndex;
          if (isRowOnDown || isRowOnMiddle) {
            this.dispatch('scrollToRowAfterCorrection', Position.end);
          }
        }
      }
    }
  }

  private getContextMenu(): JSX.Element {
    let { contextMenu } = this.props;
    let menu: any = null;
    if (Array.isArray(contextMenu) && contextMenu.length) {
      let [Component, props = {}] = contextMenu;
      _.assign(props, {
        offset: this.state.offset,
        show: this.state.showContextMenu,
        closeMenuHandler: this.closeContextMenu,
        contextMenuItem: this.state.contextMenuItem
      });
      menu = <Component {...props} />;
    }
    return menu;
  }

  /**
   * Renders when there is no records in grid if emptyGrid configuration is passed via props.
   */
  private getGridNoRecords(dataLoadDone: boolean, data: any[]) {
    const { emptyGrid = {} } = this.props;
    const { img, text = this.translate('emptyGridDefaultText') } = emptyGrid;
    return (
      <GridNoRecords>
        {dataLoadDone && !data.length ?
          (
            <div
              className="k-vbox k-justify-content-center k-align-items-center"
            >
              {img ? <div
                className={`tp-grid-empty-state-img ${img}`}
              /> : null}
              <div
                className="tp-grid-empty-state-text k-text-center tp-font-size-xl"
              >
                {text}
              </div>
            </div>
          ) : (<div></div>)
        }
      </GridNoRecords>
    );
  }

  private isPartOfNavigationGrid(event: KeyboardEvent) {
    let partOfGrid = this.getDomNode()?.contains(document?.activeElement);
    if (partOfGrid === false) {
      const nodeType: string = _.get(event.target, 'nodeName', '').toLowerCase();
      partOfGrid = nodeType === 'body';
    }
    return partOfGrid;
  }

  /**
   * Map for custom cell renderers. Matching with dataIndex from column object
   */
  private CELL_RENDER_MAP: any = {
    actions: CellActions,
    button: CellButton,
    checkbox: CellCheckbox,
    count: CellCount,
    date: CellDate,
    icon: CellIcon,
    link: CellLink,
    routeLink: CellRouteLink,
    progress: CellProgress,
    tag: CellTag,
    text: CellText,
    textCounterTag: CellTextCounterTag,
    segment: CellSegment,
    reorderingRows: CellReorderingRows,
    expandDetails: CellExpandDetails,
    ellipsisExpandText: CellEllipsisExpandText
  };

  private HEADER_CELL_RENDER_MAP: any = {
    icon: HeaderCellIcon,
    checkbox: HeaderCellCheckbox
  };

  public getIdGetter = () => {
    const selectableAllUniqueField = this.props.scrollable === 'virtual' ? this.selectableAllUniqueField : this.props.uniqueField;
    return getter(selectableAllUniqueField);
  }

  /**
   * Custom header `onChange` event handler
   * @param {CheckboxChangeEvent} event
   */
  private onHeaderChange = (event: CheckboxChangeEvent) => {
    if (this.props.selectableAll) {
      const { selectableAll } = this.props;
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.element.checked;
      let newData = [];
      const selectedState = {};
      let uniqueField;
      this.state.data.forEach((item) => {
        uniqueField = this.getIdGetter()(item);
        if (!item.disabled) {
          selectedState[uniqueField] = checked;
        } else {
          selectedState[uniqueField] = _.isUndefined(selectedState[uniqueField]) ? item[selectableAll] : selectedState[uniqueField];
        }
        const newItem = { ...item };
        if (!item.disabled) {
          newItem[selectableAll] = checked;
        } else {
          newItem[selectableAll] = item[selectableAll];
        }
        newData.push(newItem);
      });
      if (_.isFunction(this.props.onCellCheckboxChange)) {
        this.props.onCellCheckboxChange(checked, {
          mode: 'all',
          data: newData,
          selectedState
        });
      }
      this.setState({
        data: newData,
        selectedState
      });
    }
  }

  /**
   * Custom checkbox cell `onChange` event handler
   * @param {Boolean} value Checkbox value (checked)
   * @param {Number} index Index of row where checkbox is located
   */
  private onCellCheckboxChange = (value: boolean, index: number) => {
    if (this.props.selectableAll) {
      const selectedState = { ...this.state.selectedState };
      if (Object.keys(selectedState).length < this.state.data.length) {
        let uniqueFieldName;
        this.state.data.forEach((item) => {
          uniqueFieldName = this.getIdGetter()(item);
          if (!_.isBoolean(selectedState[uniqueFieldName])) {
            selectedState[uniqueFieldName] = false;
          }
        });
      }

      selectedState[this.getIdGetter()(this.state.data[index])] = value;
      this.setState({ selectedState }, () => {
        if (_.isFunction(this.props.onCellCheckboxChange)) {
          this.props.onCellCheckboxChange(value, {
            index,
            mode: 'single',
            data: [...this.state.data],
            selectedState
          });
        }
      });
    }
  }

  /**
   * Adds row to grid
   * @param {Object} dataItem
   */
  addRow = (dataItem = {}) => {
    let data = [...this.state.data];
    data.push({
      ...dataItem
    });
    data = this.getDisabledAllCheckboxData(data).data;
    let selectedState = {};
    if (this.props.selectableAll) {
      selectedState = { ...this.state.selectedState };
      selectedState[this.getIdGetter()(data[data.length - 1])] = dataItem[this.props.selectableAll];
    }
    if (_.isFunction(this.props.onAddRow)) {
      this.props.onAddRow(dataItem, {
        data,
        selectedState
      });
    }
    this.setState({
      data,
      selectedState
    });
  }

  /**
   * Returns grid data
   */
  getGridData = () => {
    let data = [...this.state.data];
    if (this.props.selectableAll) {
      data = data.map(item => ({
        ...item,
        [this.props.selectableAll]: this.state.selectedState[this.getIdGetter()(item)]
      }));
    }
    return data;
  }

  insertRecords = (records, index) => {
    if (this.props.insertRecords) {
      this.props.insertRecords(records, index);
    }
  }

  /**
   * Reorder rows
   * @param {Object} dataItem
   * @param {String} direction There can be 'before' or 'after'
   */
  reorder = (dataItem: any, direction: 'before' | 'after') => {
    if (this.getIdGetter()(dataItem) === this.getIdGetter()(this.state.activeItem)) {
      return;
    }
    let reorderedData = this.state.data.slice();
    let prevIndex = reorderedData.findIndex(p => this.getIdGetter()(p) === this.getIdGetter()(this.state.activeItem));
    let nextIndex = reorderedData.findIndex(p => this.getIdGetter()(p) === this.getIdGetter()(dataItem));
    reorderedData.splice(prevIndex, 1);
    reorderedData.splice(
      Math.max(nextIndex + (direction === 'before' ? -1 : 0), 0),
      0,
      this.state.activeItem || reorderedData[0]
    );
    if (this.props.getReorderingRowsData) {
      reorderedData = this.props.getReorderingRowsData(reorderedData, dataItem, direction);
    }
    this.setState({ data: reorderedData });
  };

  dragStart = (dataItem: any) => {
    this.setState({
      data: this.state.data,
      activeItem: dataItem
    });
  };

  /**
   * Fires when a column is resized and reconfigure grid columns
   * @param {GridColumnResizeEvent} event
   */
  private onColumnResize = (event: GridColumnResizeEvent) => {
    if (this.props.onColumnResize) {
      this.props.onColumnResize(event);
    } else {
      let { columns, end, index, newWidth } = event;
      const changedColumn = columns[index];
      if (changedColumn) {
        const { field } = changedColumn;
        if (newWidth === 0) {
          newWidth = changedColumn['width'] as number;
        }
        if (end) {
          this.dispatch('ReconfigureColumns', Position.end, {
            columns: this.props.columns.map((column) => {
              if (column.dataIndex === field) {
                column = {
                  ...column,
                  width: newWidth
                };
              }
              return column;
            })
          });
        }
      }
    }
    if (this.gridRef.vs?.table && this.props.autoHeightRowCorrection) {
      const { data, skip } = this.state;
      const { rowHeight } = this.props;
      this.scrollCorrectionServiceVS.setCorrection(this.gridRef.vs, skip, skip, data, rowHeight, this);
    }
  };

  /**
   * Returns main rendering body
   */
  getRenderMainBody = () => {
    let {
      pageSize,
      rowHeight = 20,
      uniqueField,
      sort: propsSort = [],
      selectableAll,
      isVirtualScrollHOC,
      reorderingRows
    } = this.props;
    let { className = '' } = this.props;
    let {
      data,
      dataLoadDone,
      skip,
      total,
      columns,
      sort
    } = this.state;

    if (reorderingRows && columns[0] && columns[0]?.cellType !== 'reorderingRows') {
      columns.unshift({
        cellType: 'reorderingRows',
        width: 40
      });
    }

    if (!isVirtualScrollHOC) {
      pageSize = data.length;
      total = pageSize;
    }

    if (propsSort.length) {
      sort = propsSort;
    }

    let dataItemKey = uniqueField ? uniqueField : undefined;

    let pageData: any[] = data.slice(skip, skip + pageSize);
    if (selectableAll) {
      pageData = pageData.map(item => ({
        ...item,
        [this.props.selectableAll]: this.state.selectedState[this.getIdGetter()(item)]
      }));
    }

    className = classnames(
      className,
      {
        [EMPTY_GRID_CLASS]: !data.length
      }
    );
    return (
      <>
        {this.getContextMenu()}
        <KendoGrid
          {...this.props}
          className={className}
          data={columns.some(column => !column.hidden) ? pageData : []}
          dataItemKey={dataItemKey}
          skip={skip}
          take={pageSize}
          rowHeight={rowHeight}
          total={total}
          // <debug>
          data-cy={'NavigationGrid'}
          // </debug>
          onColumnResize={this.onColumnResize}
          onRowClick={this.rowClick}
          onRowDoubleClick={this.rowDoubleClick}
          onScroll={this.onScroll}
          onSelectionChange={this.onSelectionChange}
          onSortChange={this.onSortChange}
          rowRender={this.rowRender}
          ref={this.setGridReference}
          sort={sort}
        >
          {this.getGridNoRecords(dataLoadDone, data)}
          {
            columns.filter(this.isColumnVisible).map((columnConfig, index) => columnConfig.isGrouped
              ? this.groupColumnMapMethod(columnConfig, index)
              : this.columnsMapMethod(columnConfig, index)
            ) || null
          }
        </KendoGrid>
        {this.props.accessibilityRowDescription &&
          <div
            style={{ display: 'none' }}
            id={'row-description'}
          >
            {`${this.translate('rowIndex')} ${this.state.activeRowIndex} ${this.props.accessibilityRowDescription}`}
          </div>
        }
      </>
    );
  }

  renderBody = () => {
    const { reorderingRows } = this.props;
    return (
      <>
        {reorderingRows ? <ReorderContext.Provider
          // @ts-ignore
          value={{ reorder: this.reorder, dragStart: this.dragStart }}
        >
          <DragAndDrop>
            {this.getRenderMainBody()}
          </DragAndDrop>
        </ReorderContext.Provider> : <>{this.getRenderMainBody()}</>}
      </>
    );
  }
}

export default NavigationBaseGrid;
